import { Mention as Base, MENTION_NODE_NAME, nodeIsMention } from "@corca/tiptap";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { ReactNodeViewRenderer } from "@tiptap/react";

import { idGenerator } from "../suggestions/idGenerator";
import { MENTION_TRIGGER } from "../suggestions/providers/mentionSuggestionsProvider";

import { MENTION_CLASS, MentionView } from "./mention/MentionView";
import { hasClickModifier } from "./utils/hasClickModifier";

import type { ChatMessageModel } from "@/models/chat/ChatMessageModel";
import type { ChatModel } from "@/models/chat/ChatModel";
import type { DocumentModel } from "@/models/DocumentModel";
import type { Range } from "@tiptap/core";
import type { DOMOutputSpec } from "@tiptap/pm/model";
import type { EditorView } from "@tiptap/pm/view";

export type MentionNavigator = (username: string) => void;

export type MentionOptions = {
  doc: DocumentModel;
  chat?: ChatModel;
  message?: ChatMessageModel;
  onOpenMention?: MentionNavigator;
  getAuthorId?: () => string | undefined;
};

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    mention: {
      appendMention: (
        position: number | Range | undefined,
        options: {
          userId: string;
          username: string;
          /**
           * Append a space after the chip.
           */
          withTrailingSpace?: boolean;
        },
      ) => ReturnType;
    };
  }
}

function getClickedMentionUsername(target: EventTarget | null): string | undefined {
  if (!(target instanceof Element)) return undefined;

  const anchor = target.closest(`a[data-type="${MENTION_NODE_NAME}"]`);
  return anchor?.getAttribute("data-username") || undefined;
}

function handleClick(
  event: MouseEvent,
  view: EditorView,
  onOpenMention?: MentionNavigator,
): boolean {
  const username = getClickedMentionUsername(event.target);
  // ignore regular links
  if (!username) return false;

  if (!(event instanceof MouseEvent)) return false;
  const modifiedClick = event.button !== 0 || hasClickModifier(event);
  if (event.defaultPrevented || view.editable || modifiedClick) {
    return modifiedClick || view.editable;
  }

  if (!onOpenMention) return false;

  event.preventDefault();
  if (event.type === "click") {
    onOpenMention(username);
  }
  return true;
}

export const Mention = Base.extend<MentionOptions>({
  renderHTML(props) {
    const rendered = this.parent?.(props) ?? ["a", props.HTMLAttributes];
    if (!Array.isArray(rendered)) return rendered;

    const [tag, parentAttributes, ...children] = rendered as [
      string,
      Record<string, unknown>,
      ...unknown[],
    ];

    const attrs: Record<string, unknown> = {
      ...parentAttributes,
      class: MENTION_CLASS,
      target: "_blank",
      rel: "noopener noreferrer nofollow",
    };

    return [tag, attrs, ...children] as DOMOutputSpec;
  },

  addNodeView() {
    return ReactNodeViewRenderer(MentionView);
  },

  addKeyboardShortcuts() {
    return {
      // Collapses the chip back to the trigger "@".
      Backspace: () =>
        this.editor.commands.command(({ tr, state, dispatch }) => {
          const { selection } = state;
          if (!selection.empty) return false;

          const { $from } = selection;
          const { nodeBefore } = $from;
          if (!nodeBefore || !nodeIsMention(nodeBefore)) return false;

          if (dispatch) {
            tr.replaceWith(
              $from.pos - nodeBefore.nodeSize,
              $from.pos,
              state.schema.text(MENTION_TRIGGER),
            );
          }

          return true;
        }),
    };
  },

  addProseMirrorPlugins() {
    const parentPlugins = this.parent?.() ?? [];

    return [
      new Plugin({
        key: new PluginKey("handleMentionClick"),
        props: {
          handleClick: (view, _pos, event) => {
            return handleClick(event, view, this.options.onOpenMention);
          },
          handleDOMEvents: {
            click: (view, event) => {
              return handleClick(event, view, this.options.onOpenMention);
            },
          },
        },
      }),
      ...parentPlugins,
    ];
  },

  addCommands() {
    return {
      appendMention:
        (position, { userId, username, withTrailingSpace = false }) =>
        ({ state, commands }) => {
          const mention = {
            type: MENTION_NODE_NAME,
            attrs: {
              id: idGenerator(),
              userId,
              username,
              authorId: this.options.getAuthorId?.(),
            },
          };

          return commands.insertContentAt(
            position !== undefined ? position : state.selection.$to.end(1),
            withTrailingSpace ? [mention, { type: "text", text: " " }] : [mention],
          );
        },
    };
  },
});
